import React, { useEffect } from "react";
import "./BlogDetail1.css";
import { FaFacebookF, FaInstagram, FaLinkedinIn,  FaCalendar, FaComments } from "react-icons/fa";

import blogBg from "../../assets/images/data.jpg"; // final bg for info section
import extraImg from "../../assets/images/blog1.png"; // enquiry left image

// Latest posts images
import post1 from "../../assets/images/post1.png";
import post2 from "../../assets/images/post2.png";
import post3 from "../../assets/images/post3.png";
import post4 from "../../assets/images/post4.png";
import post5 from "../../assets/images/post5.png";
import post6 from "../../assets/images/post6.png";

const BlogDetail1 = () => {
  // ========= UPDATED SCROLL ANIMATION FUNCTIONALITY (REPEATABLE) ==========
  useEffect(() => {
    const classesToObserve = [
      ".slide-in-left-fast",
      ".slide-in-top-smooth",
      ".slide-in-right-subtle",
      ".slide-in-left",
    ];

    const elements = document.querySelectorAll(classesToObserve.join(", "));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          } else {
            entry.target.classList.remove("active");
          }
        });
      },
      { threshold: 0.2 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* ================================= HERO SECTION ================================= */}
      <section
        className="blog-hero-section"
        style={{ backgroundImage: `url(${blogBg})` }}
      >
        <div className="blog-hero-overlay slide-in-left-fast">
          <span className="blog-category">Blog, Business</span>
          <h1 className="blog-title">
            From Numbers to Knowledge: <br /> The Art of Data Science
          </h1>
          <div className="blog-meta">
            <p><FaCalendar /> December 16, 2024</p>
            <p><FaComments /> No Comments</p>
          </div>
        </div>
      </section>

      {/* ======================== ENQUIRY + NEWSLETTER SECTION ======================== */}
      <section className="enquiry-section slide-in-top-smooth">
        <div
          className="enquiry-left"
          style={{ backgroundImage: `url(${extraImg})` }}
        />
        <div className="enquiry-right">
          <h2 className="enquiry-title">Newsletter</h2>
          <p className="enquiry-text">
            Get free tips and resources right in your inbox, along with 10,000+ others
          </p>
          <form className="enquiry-form">
            <input type="text" placeholder="Name" />
            <input type="email" placeholder="Email" />
            <button type="submit">Sign Up</button>
          </form>
        </div>
      </section>

      {/* ============================ INFO SECTION ============================ */}
      <section
        className="info-section"
        style={{ backgroundImage: `url(${blogBg})` }}
      >
        <div className="info-overlay"></div>
        <div className="info-content slide-in-right-subtle">
          <h2 className="info-heading">Understanding Data Science</h2>
          <p>
            In an age dominated by data, the ability to transform raw numbers
            into actionable insights has become a sought-after skill. Data
            science, often described as the intersection of statistics, computer
            science, and domain expertise, is more than just crunching
            numbers—it is an art that turns chaos into clarity.
          </p>
          <p>
            <strong>The Essence of Data Science:</strong> At its core, data
            science involves collecting, cleaning, analysing, and interpreting
            vast amounts of data. But beyond the technicalities lies a deeper
            goal: uncovering the stories hidden within the numbers. Data
            scientists are modern-day storytellers who bridge the gap between
            complexity and understanding. They transform an overwhelming flood
            of information into knowledge that drives decisions.
          </p>
        </div>
      </section>

      {/* ============================ LATEST POSTS SECTION ============================ */}
      <section className="latest-posts-section slide-in-left">
        <div className="latest-posts-container">
          <div className="posts-list">
            <h2 className="posts-heading">Latest Posts</h2>

            {[{img: post1, title: "Data Science", desc: "From Numbers to Knowledge: The Art of Data Science", date: "16 December 2024"},
              {img: post2, title: "Empower Your Business with NMD Infotech’s Data Analytics Services", date: "16 December 2024"},
              {img: post3, title: "NMD Infotech Services", desc: "Unlocking Business Potential: How NMD Infotech Services Can Propel Your Growth", date: "12 December 2024"},
              {img: post4, title: "Digital Marketing vs Traditional Marketing", date: "31 March 2024"},
              {img: post5, title: "Tips for Digital Marketing", date: "27 March 2024"},
              {img: post6, title: "Tip’s Of Digital Marketing For Small Business", date: "March 2014"}].map((post, idx) => (
              <div key={idx} className="post-item">
                <img src={post.img} alt={post.title} />
                <div className="post-info">
                  <h3>{post.title}</h3>
                  {post.desc && <p>{post.desc}</p>}
                  <span>{post.date}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="posts-image">
            <img src={extraImg} alt="Blog Highlight" />
          </div>
        </div>
      </section>

      {/* ============================ DATA SCIENCE FULL CONTENT SECTION ============================ */}
      <section className="ds-journey-section slide-in-top-smooth">
        <div className="ds-container">
          <h2 className="ds-title">The Journey: From Numbers to Knowledge</h2>

          <div className="ds-box">
            <h3>1. Data Collection</h3>
            <p>
              Data science begins with the raw material: <span className="highlight">data</span>.
              This data can come from diverse sources such as databases, IoT devices,
              social media, and surveys. The challenge is not just gathering data but
              ensuring its <span className="highlight">relevance</span> and <span className="highlight">reliability</span>.
            </p>
          </div>

          <div className="ds-box">
            <h3>2. Data Cleaning</h3>
            <p>
              Raw data is rarely ready for analysis. It often contains errors,
              inconsistencies, and missing values. The art lies in cleaning and
              organizing this data without losing valuable information. A well-prepared
              dataset becomes the foundation of <span className="highlight">meaningful insights</span>.
            </p>
          </div>

          <div className="ds-box">
            <h3>3. Data Analysis</h3>
            <p>
              Analysis involves identifying patterns, trends, and relationships within
              the data. Statistical models, machine learning algorithms, and
              visualization techniques come into play here. This step transforms
              numbers into <span className="highlight">actionable insights</span>.
            </p>
          </div>

          <div className="ds-box">
            <h3>4. Data Interpretation</h3>
            <p>
              Insights are valuable only when they can be understood and acted upon.
              This phase translates technical findings into <span className="highlight">practical knowledge</span>
              aligned with organizational goals.
            </p>
          </div>

          <h2 className="ds-title-small">The Tools of the Trade</h2>
          <div className="ds-box">
            <p><strong>Programming Languages:</strong> Python and R for data manipulation and analysis.</p>
            <p><strong>Visualization Tools:</strong> Tableau, Power BI, and Matplotlib for converting insights into visuals.</p>
            <p><strong>Machine Learning Frameworks:</strong> TensorFlow, Scikit-learn, and PyTorch for predictive modeling.</p>
            <p><strong>Big Data Technologies:</strong> Hadoop, Spark, and cloud platforms for massive dataset processing.</p>
          </div>

          <h2 className="ds-title-small">The Artful Side of Data Science</h2>
          <div className="ds-box">
            <p>
              Exceptional data scientists think creatively, challenge assumptions, and
              craft narratives that resonate with diverse audiences. It’s not just
              about algorithms—it's about asking the <span className="highlight">right questions</span>
              and exploring multiple perspectives.
            </p>
          </div>

          <h2 className="ds-title-small">The Impact of Data Science</h2>
          <div className="ds-box">
            <p>
              From improving healthcare outcomes to optimizing supply chains, data science
              has transformed industries. Companies use it to understand customer behaviours,
              predict market trends, and make <span className="highlight">data-driven decisions</span>.
            </p>
          </div>

          <h2 className="ds-title-small">The Future of Data Science</h2>
          <div className="ds-box">
            <p>
              With AI, edge computing, and real-time analytics rising, the future of
              data science is evolving rapidly—reshaping how organizations interact
              with data.
            </p>
          </div>

          <h2 className="ds-title-small">Conclusion</h2>
          <div className="ds-box">
            <p>
              Data science is more than a technical discipline—it is a transformative
              process that turns raw numbers into <span className="highlight">actionable knowledge</span>.
              It empowers individuals and organizations to see clearly and act decisively.
            </p>
          </div>
        </div>
      </section>

      {/* ============================ POST COMMENT & SHARE SECTION ============================ */}
      <section className="share-comment-section slide-in-top-smooth">
        <div className="share-box">
          <h3>Share it:</h3>
          <div className="share-icons">
            <a href="https://www.facebook.com/digitalmarketingnmd?mibextid=2JQ9oc" className="icon fb"><FaFacebookF /></a>
            <a href="https://www.instagram.com/nmdinfotech/?igsh=M2lnM205YXZ6b3lp#" className="icon tw"><FaInstagram /></a>
            <a href="https://in.linkedin.com/company/nmd-infotech" className="icon ln"><FaLinkedinIn /></a>
            {/* <a href="#" className="icon wa"><FaWhatsapp /></a> */}
          </div>
        </div>

        <div className="glass-form">
          <h2>Leave a Reply</h2>
          <p>Your email address will not be published. Required fields are marked *</p>

          <form
            action="https://api.web3forms.com/submit"
            method="POST"
            className="webform3"
          >
            <input type="hidden" name="access_key" value="YOUR_ACCESS_KEY_HERE" />
            <input type="hidden" name="subject" value="New Blog Comment Submitted" />

            <label>Comment *</label>
            <textarea name="comment" placeholder="Write your comment..." required></textarea>

            <label>Name *</label>
            <input type="text" name="name" placeholder="Enter your name" required />

            <label>Email *</label>
            <input type="email" name="email" placeholder="Enter your email" required />

            <label>Website</label>
            <input type="text" name="website" placeholder="Your website (optional)" />

            <div className="checkbox-row">
              <input type="checkbox" />
              <span>Save my info for next time.</span>
            </div>

            <button className="submit-btn">Post Comment</button>
          </form>
        </div>
      </section>
    </>
  );
};

export default BlogDetail1;
